import {
  AuthserverDepartmentGroup,
  AuthserverDepartmentGroups,
  AuthserverPph21MarriageCategories,
  AuthserverPph21MarriageCategory,
  AuthserverShiftTemplate,
  AuthserverShiftTemplates,
  AuthserverUserShifts,
} from "../masterbigsystem";
import {
  App,
  Bank,
  Company,
  Department,
  ExtHrEmployee,
  Role,
  User,
  UserApp,
} from "../model";
import { ExtTalentaUser } from "./helpers";

export const fetchUsers = async (
  baseUrl: string,
  apiKey: string,
  opts?: { showHidden?: boolean }
) => {
  try {
    return (await (
      await fetch(
        `${baseUrl}/users/view?showHidden=${opts?.showHidden ?? false}`,
        {
          headers: {
            authorization: apiKey ?? "",
          },
        }
      )
    ).json()) as User[];
  } catch (e) {
    return [];
  }
};

export const fetchShifts = async (apiKey: string) => {
  try {
    const resp = await fetch(
      `${process.env.REACT_APP_BASE_URL}/employeeshifts`,
      {
        headers: {
          authorization: apiKey ?? "",
        },
      }
    );

    return AuthserverUserShifts.fromPartial(await resp.json());
  } catch (e) {
    return AuthserverUserShifts.fromPartial({});
  }
};

export const fetchLocationRecordsHistoryRaw = async (
  apiKey: string,
  params: { from?: any; days?: any }
) => {
  try {
    const resp = await fetch(
      `${process.env.REACT_APP_LOCATION_URL}/locationrecords-raw?from=${
        params.from ?? ""
      }&days=${params.days ?? ""}`,
      {
        headers: {
          authorization: apiKey ?? "",
        },
      }
    );

    return (await resp.json()) as any[];
  } catch (e) {
    return [];
  }
};

export const fetchLocationCoordinates = async (apiKey: string) => {
  try {
    const resp = await fetch(
      `${process.env.REACT_APP_LOCATION_URL}/coordinates`,
      {
        headers: {
          authorization: apiKey ?? "",
        },
      }
    );

    return (await resp.json()) as any[];
  } catch (e) {
    return [];
  }
};

export const fetchShiftTemplates = async (apiKey: string) => {
  try {
    const resp = await fetch(
      `${process.env.REACT_APP_BASE_URL}/employeeshifttemplates`,
      {
        headers: {
          authorization: apiKey ?? "",
        },
      }
    );

    return AuthserverShiftTemplates.fromPartial(await resp.json());
  } catch (e) {
    return AuthserverShiftTemplates.fromPartial({});
  }
};

export const fetchPph21MarriageCategories = async (apiKey: string) => {
  try {
    const resp = await fetch(
      `${process.env.REACT_APP_BASE_URL}/pph21marriagecategories-proto`,
      {
        headers: {
          authorization: apiKey ?? "",
        },
      }
    );

    return AuthserverPph21MarriageCategories.fromPartial(await resp.json());
  } catch (e) {
    return AuthserverPph21MarriageCategories.fromPartial({});
  }
};

export const fetchUser = async (
  id: number,
  baseUrl: string,
  apiKey: string
) => {
  try {
    return (await (
      await fetch(`${baseUrl}/users/${id}/view`, {
        headers: {
          authorization: apiKey ?? "",
        },
      })
    ).json()) as User;
  } catch (e) {
    return null;
  }
};

export const fetchHrEmployees = async (baseUrl: string, apiKey: string) => {
  try {
    console.log("HR URL", baseUrl);
    return (await (
      await fetch(`${baseUrl}/ext-hr-employees`, {
        headers: {
          authorization: apiKey ?? "",
        },
      })
    ).json()) as ExtHrEmployee[];
  } catch (e) {
    return [];
  }
};

export const fetchCompanies = async (apiKey: string) => {
  try {
    return (await (
      await fetch(`${process.env.REACT_APP_BASE_URL}/companies`, {
        headers: {
          authorization: apiKey ?? "",
        },
      })
    ).json()) as Company[];
  } catch (e) {
    return [];
  }
};

export const fetchHrEmployeesDetail = async (
  baseUrl: string,
  apiKey: string
) => {
  try {
    console.log("HR URL", baseUrl);
    return (await (
      await fetch(`${baseUrl}/ext-hr-employees-detail`, {
        headers: {
          authorization: apiKey ?? "",
        },
      })
    ).json()) as ExtHrEmployee[];
  } catch (e) {
    return [];
  }
};

export const fetchBanks = async (props: {
  baseUrl?: string;
  apiKey?: string;
}) => {
  try {
    return (await (
      await fetch(`${props?.baseUrl}/banks`, {
        headers: {
          authorization: props?.apiKey ?? "",
        },
      })
    ).json()) as Bank[];
  } catch (e) {
    return [];
  }
};

export const fetchUserApps = async (
  id: number,
  baseUrl: string,
  apiKey: string
) => {
  try {
    return (await (
      await fetch(`${baseUrl}/users/${id}/apps`, {
        headers: {
          authorization: apiKey ?? "",
        },
      })
    ).json()) as UserApp[];
  } catch (e) {
    return [];
  }
};

export const fetchAllUserApps = async (baseUrl: string, apiKey: string) => {
  try {
    return (await (
      await fetch(`${baseUrl}/userapps`, {
        headers: {
          authorization: apiKey ?? "",
        },
      })
    ).json()) as UserApp[];
  } catch (e) {
    return [];
  }
};

export const fetchInitialData = async (baseUrl: string, apiKey: string) => {
  const [departments, roles] = await Promise.all([
    fetchDepartments(baseUrl, apiKey),
    fetchRoles(baseUrl, apiKey),
  ]);

  return {
    departments: departments,
    roles: roles,
  };
};

export const fetchDepartments = async (baseUrl: string, apiKey: string) => {
  try {
    return (await (
      await fetch(`${baseUrl}/departments`, {
        headers: { authorization: apiKey },
      })
    ).json()) as Department[];
  } catch (e) {
    return [];
  }
};

export const fetchDepartmentGroupsProto = async (params?: {
  apiKey?: string;
}) => {
  try {
    const resp = await fetch(
      `${process.env.REACT_APP_BASE_URL}/departmentgroups`,
      {
        headers: { authorization: params?.apiKey ?? "" },
      }
    );

    if (resp.status !== 200) {
      throw await resp.text();
    }

    return AuthserverDepartmentGroups.fromPartial(await resp.json());
  } catch (e) {
    return AuthserverDepartmentGroups.fromPartial({ groups: [] });
  }
};

export const fetchDepartmentGroupProto = async (params?: {
  apiKey?: string;
  id?: any;
}) => {
  try {
    const resp = await fetch(
      `${process.env.REACT_APP_BASE_URL}/departmentgroups/${params?.id}`,
      {
        headers: { authorization: params?.apiKey ?? "" },
      }
    );

    if (resp.status !== 200) {
      throw await resp.text();
    }

    return AuthserverDepartmentGroup.fromPartial(await resp.json());
  } catch (e) {
    return AuthserverDepartmentGroup.fromPartial({ departments: [] });
  }
};

export const fetchRoles = async (baseUrl: string, apiKey: string) => {
  try {
    return (await (
      await fetch(`${baseUrl}/roles`, { headers: { authorization: apiKey } })
    ).json()) as Role[];
  } catch (e) {
    return [];
  }
};

export const fetchApps = async (baseUrl: string, apiKey: string) => {
  try {
    return (await (
      await fetch(`${baseUrl}/apps`, { headers: { authorization: apiKey } })
    ).json()) as App[];
  } catch (e) {
    return [];
  }
};

export const fetchApp = async (id: number, baseUrl: string, apiKey: string) => {
  try {
    return (await (
      await fetch(`${baseUrl}/apps/${id}`, {
        headers: { authorization: apiKey },
      })
    ).json()) as App;
  } catch (e) {
    return null;
  }
};

export const fetchWaLogs = async (params: {
  apiKey?: string | null;
  from?: string | null;
  to?: string | null;
}) => {
  try {
    return (await (
      await fetch(
        `${process.env.REACT_APP_BASE_URL}/walogs?from=${
          params.from ?? ""
        }&to=${params.to ?? ""}`,
        {
          headers: { authorization: params.apiKey ?? "" },
        }
      )
    ).json()) as any[];
  } catch (e) {
    return null;
  }
};

export const fetchWaBlacklists = async (params: {
  apiKey?: string | null;
}) => {
  try {
    return (await (
      await fetch(
        `${process.env.REACT_APP_BASE_URL}/whatsappserviceblacklists`,
        {
          headers: { authorization: params.apiKey ?? "" },
        }
      )
    ).json()) as any[];
  } catch (e) {
    return null;
  }
};


export const fetchExtTalentaUsers = async (
  baseUrl: string,
  apiKey: string
): Promise<ExtTalentaUser[]> => {
  try {
    const resp = await fetch(`${baseUrl}/ext-talenta-users`, {
      headers: {
        authorization: apiKey,
        "content-type": "application/json",
      },
    });

    if (!resp.ok) return [];
    return (await resp.json()) as ExtTalentaUser[];
  } catch (e) {
    console.error(e);
    return [];
  }
};